import { FilterDescriptor, filterBy } from "@progress/kendo-data-query";
import {
  MultiSelect,
  MultiSelectBlurEvent,
  MultiSelectFilterChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { FieldRenderProps } from "@progress/kendo-react-form";
import { Hint, Error } from "@progress/kendo-react-labels";
import { useEffect, useState } from "react";
import { sortDataFunc } from "../../../utils/dropdownSort";

interface CustomMultiSelectProps extends Omit<FieldRenderProps, ""> {
  onFocus?: () => void;
  onBlur?: () => void;
  validationMessage?: string | null;
  touched?: boolean;
  modified?: boolean;
  visited?: boolean;
  valid?: boolean;
  children?: any;
}

const CustomMultiSelect = (fieldRenderProps: CustomMultiSelectProps) => {
  const {
    validationMessage,
    touched,
    modified,
    label,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    max,
    rows,
    value,
    showTextLimitHint,
    loading,
    data,
    textField,
    sorting = false, // if true, sorting enabled.
    sortField, // if 'data' is array of object then send the field name (on the basis of which you want sorting) in 'sortField' prop as string . if 'data' is array of primitive datatype then no need to send any 'sortField' prop.
    sortOrder = "asc", // send 'asc' or 'desc' for ascending or descending sorting respectively.
    filtering = false, // if true, filtering enabled.
    filterFields, // send those field names in the form of array of strings on the basis of which you want filtering. If no 'filterFields' prop is sent then kendo would take 'textField' prop to do the filtering. If no 'textField' and no 'filterFields' props are there, then no filtering will be done.
    onBlur,
    shouldRetainFilterText = false, //if true, then user will be able to enter some custom text in the multiselect field. If false (by default), then any custom text will be removed from the multiselect field as soon as the user clicks anywhere outside of the multiselect field.
    ...others
  } = fieldRenderProps;

  // NOTE -> If you want sorting based on 'firstName, lastName', and if both are not available,
  // then 'email'. For this, you can send 'firstlastname' as the sortField.

  const [filterVal, setFilterVal] = useState<string>("");
  const [items, setItems] = useState(data?.slice());
  const [sortedItems, setSortedItems] = useState(data?.slice());
  const [sortData, setSortData] = useState(data?.slice());
  const showValidationMessage = modified && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hindId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";

  useEffect(() => {
    setInitialData();
  }, [data]);

  const setInitialData=()=>{
    sorting && setSortData(sortDataFunc(data, sortField, sortOrder));
    sorting && setSortedItems(sortDataFunc(data, sortField, sortOrder));
    setItems(data?.slice());
  }

  const filterData = (filter: FilterDescriptor) => {
    const newData = sorting ? sortData?.slice() : data?.slice();
    const filteredData =
      filterFields && filterFields?.length > 0
        ? newData.filter((row: any) =>
          {
            var isCorrect = false;
            if(filterFields?.includes('firstName')){
              var fullName = (row.firstName + ' ' + row.lastName).toLowerCase();
              if(fullName.includes(filter.value.toLowerCase())){
                isCorrect = true;
              }                  
            }              
            Object.entries(row).some(([key, value]: [string, any]) => {
              if (filterFields?.includes(key) && typeof value === "string") {            
                if(!isCorrect){
                  isCorrect = value.toLowerCase().includes(filter.value.toLowerCase());
                }
              }                
            })              
            return isCorrect;
          }
          )
        : filterBy(newData, filter);
    return filteredData;
  };

  const filterChange = (event: MultiSelectFilterChangeEvent) => {
    setFilterVal(event.filter.value);
    if (sorting) {
      setSortedItems(filterData(event.filter));
    } else {
      setItems(filterData(event.filter));
    }
  };

  const handleBlur = (event:MultiSelectBlurEvent) => {
    if(!shouldRetainFilterText && filtering){
      setFilterVal("");
      setInitialData();
    }
    if(onBlur){
      onBlur();
    }
  };

  return (
    <div className="multiselectBox multiselectArrowIcon">
      <div className={"k-form-field-wrap position-relative"}>
        <MultiSelect
          style={{ zIndex: 1 }}
          onFilterChange={filterChange}
          filterable={filtering}
          loading={loading}
          filter={shouldRetainFilterText || !filtering ? undefined : filterVal}
          data={sorting ? sortedItems : items}
          value={value}
          disabled={disabled}
          textField={textField}
          onBlur={handleBlur}
          {...others}
        />
        {showTextLimitHint && (
          <Hint direction={"end"}>
            {value.length} / {max}
          </Hint>
        )}
        {showHint && <Hint id={hindId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </div>
    </div>
  );
};

export default CustomMultiSelect;
